import React from 'react';
import SwipeSlider from '@wix/da-shared-react/pkg/SwipeSlider';
import { withTranslation, WithTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-types-papi';
import { Url, USER_PROFILE_SHOP_SUB_PATHS } from '@wix/da-url';
import { MeasuredSlider as Slider } from '@wix/da-shared-react/pkg/Slider';
import { PANEL_HEIGHT } from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Shop';
import { ShopItemType } from '../../../../../../types/shop';
import DuperbrowseContext from '../../../../../contexts/DuperbrowseContext';
import { withMobileContext } from '@wix/da-react-context/pkg/MobileContext';
import {
  withVerification,
  WithVerificationProps,
} from '@wix/da-shared-react/pkg/VerificationPopup/withVerification';
import {
  withLogBiEvent,
  WithLogBiEventProps,
} from '@wix/da-bi/pkg/hocs/withLogBiEvent';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import WidgetBox, {
  Props as WidgetBoxProps,
} from '../../../../UserWidgets/_partials/WidgetBox';
import CommissionThumbWithMenu from '../../_partials/CommissionThumbWithMenu';
import Button from '@wix/da-shared-react/pkg/Button';
import EmptyState from '../../_partials/EmptyState';
import DisabledOverlay from '../../_partials/DisabledOverlay';
import DisabledEmptyState from '../../_partials/DisabledEmptyState';

import * as s from './CommissionSlider.scss';

export interface Props {
  streamId: string;
  moduleId: number;
  commissions: PapiDeviation[];
  total: number;
  isOpenForCommissions: boolean;
  isOwner: boolean;
  username: string;
  isMobile?: boolean;
  openCommissionEditModal: (params?: any) => void;
}

export class CommissionSlider extends React.Component<
  Props &
    WidgetBoxProps &
    WithTranslation &
    WithVerificationProps &
    WithLogBiEventProps
> {
  render() {
    const {
      isMobile,
      t,
      streamId,
      widgetMoveUp,
      widgetMoveDown,
      widgetRemove,
      moduleId,
      commissions,
      total,
      isOpenForCommissions,
      isOwner,
      username,
      section,
      zone,
    } = this.props;

    const isEmpty = !commissions.length;
    const isClosedForCommissions = !isOpenForCommissions;
    // is closed for commissions - visitor
    // is closed for commissions - mobile
    const showDisabledState =
      (!isOwner && isClosedForCommissions) ||
      (isMobile && isClosedForCommissions);

    const shouldRenderEmptyView = isEmpty || showDisabledState;

    return (
      <WidgetBox
        section={section}
        zone={zone}
        header={t!('common.commissions_plural')}
        headerExtras={
          isOwner &&
          !isMobile && (
            <Button
              className={s['add-new']}
              view="b16-undisciplined-designers"
              onClick={this.openCommissionEditModalFromHeaderButton}
              disabled={isClosedForCommissions && !isEmpty}
            >
              {t!('common.addNew')}
            </Button>
          )
        }
        count={total}
        moduleId={moduleId}
        widgetMoveUp={widgetMoveUp}
        widgetMoveDown={widgetMoveDown}
        widgetRemove={widgetRemove}
        isEmpty={shouldRenderEmptyView}
        hasMore={!isEmpty} // until we implement inner slider overflow recognition
        emptyView={
          showDisabledState
            ? this.renderDisabledEmptyView()
            : this.renderEmptyView()
        }
        seeAllUrl={Url.userShopPath(
          username,
          USER_PROFILE_SHOP_SUB_PATHS.COMMISSIONS
        )}
      >
        <DuperbrowseContext
          streamId={streamId}
          deviations={commissions}
          parent={{ title: t('duperbrowse.backButton.profile.shop') }}
        >
          {isMobile ? (
            <SwipeSlider>
              {commissions.map(commission => (
                <CommissionThumbWithMenu
                  key={commission.deviationId}
                  deviation={commission}
                />
              ))}
            </SwipeSlider>
          ) : (
            <div className={s['content']}>
              {isClosedForCommissions && <DisabledOverlay />}
              <Slider
                className={s['slider']}
                renderSliderItems={elementSize =>
                  commissions.map(commission => (
                    <CommissionThumbWithMenu
                      key={commission.deviationId}
                      deviation={commission}
                      width={elementSize.width}
                      height={elementSize.height + PANEL_HEIGHT}
                    />
                  ))
                }
              />
            </div>
          )}
        </DuperbrowseContext>
      </WidgetBox>
    );
  }

  // - disabled state for visitor
  // - disabled mobile state
  renderDisabledEmptyView = () => <DisabledEmptyState view="wide" />;

  // - empty commissions
  renderEmptyView() {
    const { t, isOwner } = this.props;
    return (
      <EmptyState
        view="wide"
        shopItemType={ShopItemType.COMMISSIONS}
        buttonLabel={
          isOwner ? t('pages.shop.emptyState.commissions.createCTA') : undefined
        }
        onButtonClick={
          isOwner ? this.openCommissionEditModalFromEmptyState : undefined
        }
      />
    );
  }

  openCommissionEditModal = (widgetName: string) => {
    const {
      isVerificationNeeded,
      pushVerificationPopup,
      openCommissionEditModal,
      logBiEvent,
    } = this.props;
    if (isVerificationNeeded) {
      pushVerificationPopup('submitcommission');
      return;
    }

    openCommissionEditModal();
    logBiEvent({
      evid: BiEventType.ADD_NEW_CONTENT_CLICK,
      widgetname: widgetName,
    });
  };

  openCommissionEditModalFromHeaderButton = () =>
    this.openCommissionEditModal('commission_headerbutton');
  openCommissionEditModalFromEmptyState = () =>
    this.openCommissionEditModal('commission_emptystate');
}

export default withVerification(
  withMobileContext(withLogBiEvent(withTranslation()(CommissionSlider)))
) as React.ComponentType<Props>;
